import { Cart } from "@/components/cart";
import {
  useEliteReservation,
  useEliteStructure,
} from "@/contexts/EliteTemporaryContextProvider";
import { useGuestAuth } from "@/contexts/GuestAuthContext";
import { cn } from "@/lib/utils";
import {
  Award,
  Globe,
  HandPlatter,
  Home,
  Info,
  LogIn,
  MapPin,
  MapPinned,
  Menu,
  Phone,
  Sparkles,
  Users,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { LanguageSelector } from "./LanguageSelector";
import { ModeToggle } from "./ThemeToggle";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";

export default function Navbar() {
  const [sheetOpen, setSheetOpen] = useState(false);
  const location = useLocation();
  const { reservation } = useEliteReservation();
  const { structure } = useEliteStructure();
  const { config, profile, openAuthModal } = useGuestAuth();
  const { t } = useTranslation("common");

  const navItems = [
    {
      to: "/",
      label: "Home",
      icon: Home,
      exact: true,
      visible: true,
    },
    {
      to: "/guest-area",
      label: config?.programName || "Guest Area & Fidelity",
      icon: Award,
      exact: false,
      visible: Boolean(config?.isEnabled),
    },
    {
      to: "/check-in",
      label: t("webCheckIn.title", "Web Check-In"),
      icon: Users,
      exact: false,
      visible: Boolean(reservation?.reservation?.id),
    },
    {
      to: "/services",
      label: t("services.title", "Servizi"),
      icon: HandPlatter,
      exact: false,
      visible: true,
    },
    {
      to: "/point-of-interest",
      label: t("pointOfInterest.title", "Punti di interesse"),
      icon: MapPinned,
      exact: false,
      visible: true,
    },
    {
      to: "/hotel-info",
      label: t("hotelInfo.title", "Info Hotel"),
      icon: Info,
      exact: false,
      visible: true,
    },
  ];

  return (
    <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80">
      <div className="container mx-auto max-w-6xl relative flex h-14 sm:h-16 items-center justify-between px-3 sm:px-4">
        {/* Left: Hamburger menu */}
        <div className="flex items-center z-10">
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-foreground"
                aria-label="Apri menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="flex w-full flex-col sm:max-w-xs px-4 py-5"
            >
              <SheetHeader className="text-left pb-3 border-b">
                <SheetTitle className="flex items-center gap-2">
                  {structure?.logo ? (
                    <img
                      src={structure.logo}
                      alt={structure.name || "Logo"}
                      className="max-h-9 max-w-[170px] object-contain"
                    />
                  ) : (
                    <span className="font-bold text-base truncate">
                      {structure?.name || "App H-Elite"}
                    </span>
                  )}
                </SheetTitle>
              </SheetHeader>

              {/* Drawer Content */}
              <div className="flex-1 overflow-y-auto py-3 flex flex-col gap-1">
                {config?.isEnabled && (
                  <div className="mb-3 rounded-xl border bg-primary/5 p-3">
                    {profile ? (
                      <Link
                        to="/guest-area"
                        onClick={() => setSheetOpen(false)}
                        className="flex items-center justify-between gap-2"
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-foreground truncate">
                            {profile.name} {profile.surname}
                          </p>
                          <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                            <span
                              className="inline-block size-2 rounded-full"
                              style={{ backgroundColor: profile.fidelityColor }}
                            />
                            {profile.fidelityType} • {Math.round(profile.currentPoints)} pt
                          </p>
                        </div>
                        <Award className="size-4 text-primary shrink-0" />
                      </Link>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                          <Sparkles className="size-3.5 text-primary" />
                          <span>{config.programName}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-1.5">
                          <Button
                            asChild
                            size="sm"
                            variant="default"
                            className="h-9 text-xs"
                          >
                            <Link
                              to="/guest-area/register"
                              onClick={() => setSheetOpen(false)}
                            >
                              Iscriviti
                            </Link>
                          </Button>
                          <Button
                            asChild
                            size="sm"
                            variant="outline"
                            className="h-9 text-xs"
                          >
                            <Link
                              to="/guest-area/login"
                              onClick={() => setSheetOpen(false)}
                            >
                              Accedi
                            </Link>
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-3 mb-1">
                  Navigazione
                </span>
                {navItems
                  .filter((item) => item.visible)
                  .map((item) => {
                    const isActive = item.exact
                      ? location.pathname === item.to
                      : location.pathname.startsWith(item.to);

                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setSheetOpen(false)}
                        className={cn(
                          "flex items-center gap-3 text-sm px-3 py-2.5 rounded-lg font-medium transition-colors",
                          isActive
                            ? "bg-primary/10 text-primary font-semibold"
                            : "text-muted-foreground hover:text-foreground hover:bg-accent"
                        )}
                      >
                        <item.icon className="h-4 w-4 shrink-0" />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}

                {/* Contatti & Posizione nella modale laterale */}
                {(structure?.tel ||
                  structure?.website ||
                  structure?.googleMapsPosition) && (
                  <>
                    <Separator className="my-3" />
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-3 mb-1">
                      {t("hotelInfo.title", "Contatti & Posizione")}
                    </span>
                    <div className="flex flex-col gap-1.5">
                      {structure?.tel && (
                        <a
                          href={`tel:${structure.tel}`}
                          className="flex items-center gap-3 text-sm px-3 py-2 rounded-lg hover:bg-accent text-foreground transition-colors border border-border/40"
                        >
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                            <Phone className="h-3.5 w-3.5" />
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-[10px] text-muted-foreground leading-tight">
                              {t("call", "Chiama")}
                            </span>
                            <span className="text-xs font-medium truncate">
                              {structure.tel}
                            </span>
                          </div>
                        </a>
                      )}

                      {structure?.website && (
                        <a
                          href={structure.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 text-sm px-3 py-2 rounded-lg hover:bg-accent text-foreground transition-colors border border-border/40"
                        >
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                            <Globe className="h-3.5 w-3.5" />
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-[10px] text-muted-foreground leading-tight">
                              {t("website", "Sito Web")}
                            </span>
                            <span className="text-xs font-medium truncate">
                              {structure.website
                                .replace(/^https?:\/\/(www\.)?/, "")
                                .replace(/\/$/, "")}
                            </span>
                          </div>
                        </a>
                      )}

                      {structure?.googleMapsPosition && (
                        <a
                          href={structure.googleMapsPosition}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 text-sm px-3 py-2 rounded-lg hover:bg-accent text-foreground transition-colors border border-border/40"
                        >
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                            <MapPin className="h-3.5 w-3.5" />
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-[10px] text-muted-foreground leading-tight">
                              {t("location", "Posizione")}
                            </span>
                            <span className="text-xs font-medium truncate">
                              {structure.address
                                ? `${structure.address}${
                                    structure.city ? `, ${structure.city}` : ""
                                  }`
                                : "Google Maps"}
                            </span>
                          </div>
                        </a>
                      )}
                    </div>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Center: Logo / Hotel Name */}
        <div className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center max-w-[42%] sm:max-w-[40%] md:max-w-[32%] pointer-events-auto">
          <Link
            to="/"
            className="flex items-center justify-center transition-opacity hover:opacity-85"
            title="Home"
          >
            {structure?.logo ? (
              <img
                src={structure.logo}
                alt={structure.name || "Logo"}
                className="max-h-8 sm:max-h-9 md:max-h-10 w-auto object-contain"
              />
            ) : (
              <span className="font-bold text-sm sm:text-base md:text-lg truncate text-foreground">
                {structure?.name || "App H-Elite"}
              </span>
            )}
          </Link>
        </div>

        {/* Right: Quick actions & Controls */}
        <div className="flex items-center gap-0.5 sm:gap-1 z-10">
          {config?.isEnabled && (
            profile ? (
              <Button
                variant="outline"
                size="sm"
                asChild
                className="h-8 px-2.5 gap-1.5 text-xs font-semibold border-primary/30 bg-primary/5"
              >
                <Link to="/guest-area" title="La tua Guest Area">
                  <Award
                    className="h-3.5 w-3.5"
                    style={{ color: profile.fidelityColor }}
                  />
                  <span>{Math.round(profile.currentPoints)} pt</span>
                </Link>
              </Button>
            ) : (
              <Button
                asChild
                variant="outline"
                size="sm"
                className="h-8 px-2.5 gap-1.5 text-xs font-medium"
              >
                <Link to="/guest-area/login" title="Accedi alla Guest Area">
                  <LogIn className="h-3.5 w-3.5 text-primary" />
                  <span className="hidden sm:inline">Guest Area</span>
                </Link>
              </Button>
            )
          )}

          {structure?.tel && (
            <Button
              variant="ghost"
              size="icon"
              asChild
              className="hidden sm:inline-flex text-muted-foreground hover:text-foreground"
            >
              <a
                href={`tel:${structure.tel}`}
                title={t("call", "Chiama")}
                aria-label={t("call", "Chiama")}
              >
                <Phone className="h-4 w-4" />
              </a>
            </Button>
          )}

          {structure?.website && (
            <Button
              variant="ghost"
              size="icon"
              asChild
              className="hidden md:inline-flex text-muted-foreground hover:text-foreground"
            >
              <a
                href={structure.website}
                target="_blank"
                rel="noopener noreferrer"
                title={t("website", "Sito Web")}
                aria-label={t("website", "Sito Web")}
              >
                <Globe className="h-4 w-4" />
              </a>
            </Button>
          )}

          {structure?.googleMapsPosition && (
            <Button
              variant="ghost"
              size="icon"
              asChild
              className="hidden md:inline-flex text-muted-foreground hover:text-foreground"
            >
              <a
                href={structure.googleMapsPosition}
                target="_blank"
                rel="noopener noreferrer"
                title={t("location", "Posizione")}
                aria-label={t("location", "Posizione")}
              >
                <MapPin className="h-4 w-4" />
              </a>
            </Button>
          )}

          <LanguageSelector />
          <ModeToggle />
          <Cart />
        </div>
      </div>
    </header>
  );
}
